import React, { useCallback, useEffect, useState } from "react";
import {
  LogOut,
  Loader2,
  Trash2,
  Image as ImageIcon,
  RefreshCw,
  Users as UsersIcon,
  Plus,
  ArrowLeft,
  X,
  User as UserIcon,
  Calendar,
  Mail,
  Lock,
  Check,
  Copy,
  BarChart3,
  Pencil,
  Crown,
  Shield,
  ShieldCheck,
  Pin,
  PinOff,
} from "lucide-react";
import { Link } from "react-router-dom";
import Lightbox from "./Lightbox";
import { useAuth, authHeaders } from "./auth";
import { useCreditAlert } from "./CreditAlert";
import Analytics from "./Analytics";
import Sessions from "./Sessions";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api`;

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* ----------------------------- GALLERY ----------------------------- */
const PAGE_SIZE = 24;

function GalleryView({ user, ttlDays }) {
  const { triggerCreditAlert, clearCreditAlert } = useCreditAlert();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [mineOnly, setMineOnly] = useState(false);
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false);
  // Quota badge — /me/quota returns exactly: per_user_24h, global_24h,
  // my_24h_count, global_24h_count, override, exempt_from_per_user_cap.
  // Null when the fetch failed or a key is missing, so we render nothing
  // rather than a wrong number.
  const [quota, setQuota] = useState(null);
  const loadQuota = useCallback(async () => {
    try {
      const res = await fetch(`${API}/me/quota`, { headers: authHeaders() });
      if (!res.ok) {
        setQuota(null);
        return;
      }
      const data = await res.json();
      const keys = [
        "per_user_24h",
        "global_24h",
        "my_24h_count",
        "global_24h_count",
        "override",
        "exempt_from_per_user_cap",
      ];
      for (const k of keys) {
        if (!(k in data)) {
          setQuota(null);
          return;
        }
      }
      setQuota(data);
    } catch {
      setQuota(null);
    }
  }, []);

  const load = useCallback(
    async (targetPage = page) => {
      setLoading(true);
      setErr("");
      try {
        const skip = targetPage * PAGE_SIZE;
        const mineParam = mineOnly ? "&mine=1" : "";
        const pinParam = pinnedOnly ? "&pinned=1" : "";
        const qParam = debouncedSearch ? `&q=${encodeURIComponent(debouncedSearch)}` : "";
        const res = await fetch(
          `${API}/renders?skip=${skip}&limit=${PAGE_SIZE}${mineParam}${pinParam}${qParam}`,
          { headers: authHeaders() }
        );
        if (res.status === 401) {
          localStorage.removeItem("hedaply_token");
          window.location.reload();
          return;
        }
        const data = await res.json();
        setItems(data.items || []);
        setTotal(typeof data.total === "number" ? data.total : (data.items || []).length);
        loadQuota();
      } catch {
        setErr("Failed to load gallery.");
      } finally {
        setLoading(false);
      }
    },
    [page, mineOnly, pinnedOnly, debouncedSearch, loadQuota]
  );

  // Debounce the search box — 400ms after the last keystroke, commit the
  // value that the fetch effect actually uses. Matches the existing
  // mineOnly/pinnedOnly page-reset pattern below.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to first page whenever any filter flips
  useEffect(() => {
    setPage(0);
  }, [mineOnly, pinnedOnly, debouncedSearch]);

  useEffect(() => {
    load(page);
  }, [load, page]);

  const open = async (id) => {
    setActiveLoading(true);
    try {
      const res = await fetch(`${API}/renders/${id}`, { headers: authHeaders() });
      const data = await res.json();
      setActive(data);
    } finally {
      setActiveLoading(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this render?")) return;
    const r = await fetch(`${API}/renders/${id}`, { method: "DELETE", headers: authHeaders() });
    if (r.ok) {
      if (active?.id === id) setActive(null);
      // Reload current page so totals + skip stay correct
      load(page);
    } else {
      const d = await r.json().catch(() => ({}));
      alert(d.detail || "Could not delete");
    }
  };

  const togglePin = async (id, currentlyPinned, e) => {
    e.stopPropagation();
    // Optimistic flip
    setItems((prev) =>
      prev.map((x) => (x.id === id ? { ...x, pinned: !currentlyPinned } : x))
    );
    const r = await fetch(`${API}/renders/${id}/pin`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ pinned: !currentlyPinned }),
    });
    if (!r.ok) {
      // Revert on failure
      setItems((prev) =>
        prev.map((x) => (x.id === id ? { ...x, pinned: currentlyPinned } : x))
      );
      const d = await r.json().catch(() => ({}));
      alert(d.detail || "Could not update pin");
    }
  };

  // Rename a render inline. Same ownership rule as the backend PATCH
  // endpoint: owner or creator. Optimistic update on the current item;
  // no page refetch.
  const startRename = (it, e) => {
    e.stopPropagation();
    setRenamingId(it.id);
    setRenameDraft(it.title || "");
  };
  const cancelRename = (e) => {
    if (e) e.stopPropagation();
    setRenamingId(null);
    setRenameDraft("");
  };
  const saveRename = async (id, e) => {
    if (e) e.stopPropagation();
    const trimmed = renameDraft.trim().slice(0, 80);
    // Optimistic set (empty string clears to null server-side)
    const nextTitle = trimmed || null;
    setItems((prev) =>
      prev.map((x) => (x.id === id ? { ...x, title: nextTitle } : x))
    );
    setRenamingId(null);
    setRenameDraft("");
    const r = await fetch(`${API}/renders/${id}/title`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ title: trimmed }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      alert(d.detail || "Could not rename");
      // Revert to whatever the row currently has server-side by
      // reloading this page — small and rare.
      load(page);
    }
  };

  const downloadActive = () => {
    if (!active) return;
    const a = document.createElement("a");
    a.href = active.image;
    a.download = `hedaply-${active.scene_id}-${active.id.slice(0, 8)}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (loading && items.length === 0) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="aspect-video animate-pulse rounded-xl bg-heda-line/50" />
        ))}
      </div>
    );
  }
  if (err) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {err}
      </div>
    );
  }
  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-heda-line bg-white py-20 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-heda-olive-tint/60 text-heda-olive">
          {pinnedOnly ? <Pin size={28} strokeWidth={1.6} /> : <ImageIcon size={28} strokeWidth={1.4} />}
        </div>
        <h2 className="font-display text-xl font-bold text-heda-ink">
          {pinnedOnly
            ? mineOnly
              ? "No pinned renders by you yet"
              : "Nothing pinned yet"
            : "No renders yet"}
        </h2>
        <p className="mt-1 max-w-sm text-sm text-heda-mute">
          {pinnedOnly
            ? "Hover any render in the gallery and tap the pin icon to save it forever — your hand-picked best work will live here."
            : "Generated designs from any team member appear here. Tap any image to open it fullscreen for the customer."}
        </p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fromIdx = page * PAGE_SIZE + 1;
  const toIdx = Math.min(total, page * PAGE_SIZE + items.length);
  // Index of the currently-open render within the CURRENT PAGE, or -1
  // when nothing is open / the active render isn't on this page. Drives
  // the Lightbox prev/next arrows (page-local navigation only).
  const activeIdx = active ? items.findIndex((x) => x.id === active.id) : -1;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-[12px] uppercase tracking-wider text-heda-mute">
          {total} render{total === 1 ? "" : "s"} · last {ttlDays ?? 30} days
          {mineOnly && (
            <span className="ml-1 rounded-full bg-heda-gold-tint px-2 py-0.5 text-[10px] font-bold tracking-wider text-heda-gold">
              MINE
            </span>
          )}
          {pinnedOnly && (
            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-heda-gold px-2 py-0.5 text-[10px] font-bold tracking-wider text-white">
              <Pin size={9} strokeWidth={2.6} /> PINNED
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Segmented All / Mine toggle — OWNER-ONLY. Backend now scopes
              staff to their own renders unconditionally (Feb 2026 access-
              control fix), so showing this toggle to staff would be
              misleading — clicking "All renders" wouldn't actually widen
              their view. */}
          {user.role === "owner" && (
            <div
              data-testid="gallery-scope"
              className="flex items-center gap-0.5 rounded-full border border-heda-line bg-white p-0.5"
            >
              <button
                data-testid="gallery-scope-all"
                type="button"
                onClick={() => setMineOnly(false)}
                className={[
                  "rounded-full px-3 py-1.5 text-[12px] font-semibold transition",
                  !mineOnly
                    ? "bg-heda-ink text-white shadow-sm"
                    : "text-heda-mute hover:text-heda-ink",
                ].join(" ")}
              >
                All renders
              </button>
              <button
                data-testid="gallery-scope-mine"
                type="button"
                onClick={() => setMineOnly(true)}
                className={[
                  "inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[12px] font-semibold transition",
                  mineOnly
                    ? "bg-heda-gold text-white shadow-sm"
                    : "text-heda-mute hover:text-heda-ink",
                ].join(" ")}
              >
                <UserIcon size={11} /> My renders
              </button>
            </div>
          )}
          {/* Pinned-only chip — combinable with All / Mine */}
          <button
            data-testid="gallery-pinned-chip"
            type="button"
            onClick={() => setPinnedOnly((v) => !v)}
            className={[
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition",
              pinnedOnly
                ? "border-heda-gold bg-heda-gold text-white shadow-sm"
                : "border-heda-line bg-white text-heda-mute hover:border-heda-gold hover:text-heda-ink",
            ].join(" ")}
            title={pinnedOnly ? "Showing only pinned renders" : "Show only pinned renders (your best work)"}
          >
            <Pin size={11} strokeWidth={pinnedOnly ? 2.4 : 2} />
            Pinned
          </button>
          {/* Rolling-24h quota badge. Renders NOTHING when /me/quota
              fails or any key is missing — never show a wrong number. */}
          {quota && (() => {
            const unlimited =
              quota.exempt_from_per_user_cap === true || quota.per_user_24h <= 0;
            const spent =
              !unlimited && quota.my_24h_count >= quota.per_user_24h;
            const pad = (n) => String(n).padStart(2, "0");
            return (
              <span
                data-testid="gallery-quota-badge"
                title={`Studio total today: ${quota.global_24h_count}/${quota.global_24h}`}
                className={[
                  "inline-flex items-center rounded-full border px-3 py-1.5 text-[12px] font-semibold transition",
                  spent
                    ? "border-red-200 bg-red-50 text-red-600"
                    : "border-heda-line bg-white text-heda-mute",
                ].join(" ")}
              >
                {unlimited ? "∞" : `${pad(quota.my_24h_count)}/${pad(quota.per_user_24h)}`}
              </span>
            );
          })()}
          {/* Search — debounced, appended as &q=... when non-empty */}
          <input
            data-testid="gallery-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search renders"
            maxLength={80}
            className="w-44 rounded-full border border-heda-line bg-white px-3 py-1.5 text-[12px] text-heda-ink outline-none transition placeholder:text-heda-mute focus:border-heda-gold"
          />
          <button
            data-testid="gallery-refresh"
            onClick={() => load(page)}
            className="inline-flex items-center gap-1.5 rounded-full border border-heda-line bg-white px-3 py-1.5 text-[12px] text-heda-ink hover:border-heda-gold"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>
      <div data-testid="admin-grid" className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {items.map((it) => {
          const canDelete = user.role === "owner" || it.created_by_email === user.email;
          return (
            <button
              key={it.id}
              type="button"
              data-testid={`admin-card-${it.id}`}
              onClick={() => open(it.id)}
              className="group relative overflow-hidden rounded-xl border border-heda-line bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:border-heda-gold hover:shadow-md"
            >
              <div className="relative aspect-video overflow-hidden bg-black">
                <img
                  src={it.thumbnail}
                  alt={it.scene_name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Always-visible pin badge when pinned */}
                {it.pinned && (
                  <span
                    data-testid={`render-pinned-badge-${it.id}`}
                    className="pointer-events-none absolute left-2 top-2 flex items-center gap-1 rounded-full bg-heda-gold/95 px-1.5 py-1 text-[9.5px] font-bold uppercase tracking-wider text-white shadow-sm backdrop-blur"
                    title="Saved forever — excluded from auto-delete"
                  >
                    <Pin size={10} strokeWidth={2.4} />
                  </span>
                )}
                {/* Hover toggle — pin / unpin */}
                <button
                  type="button"
                  data-testid={`render-pin-toggle-${it.id}`}
                  onClick={(e) => togglePin(it.id, !!it.pinned, e)}
                  className={[
                    "absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full backdrop-blur transition",
                    it.pinned
                      ? "bg-black/55 text-white hover:bg-black/70"
                      : "bg-black/45 text-white hover:bg-black/65",
                  ].join(" ")}
                  title={it.pinned ? "Unpin (allow auto-delete)" : "Save forever"}
                >
                  {it.pinned ? <PinOff size={13} /> : <Pin size={13} />}
                </button>
              </div>
              <div className="flex items-start justify-between gap-2 px-3 py-2 text-left">
                <div className="min-w-0 flex-1">
                  {renamingId === it.id ? (
                    <div
                      className="flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        data-testid={`render-rename-input-${it.id}`}
                        autoFocus
                        value={renameDraft}
                        maxLength={80}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveRename(it.id, e);
                          if (e.key === "Escape") cancelRename(e);
                        }}
                        placeholder={it.scene_name}
                        className="min-w-0 flex-1 rounded border border-heda-line bg-white px-1.5 py-0.5 text-[12px] text-heda-ink outline-none focus:border-heda-gold"
                      />
                      <button
                        type="button"
                        data-testid={`render-rename-save-${it.id}`}
                        onClick={(e) => saveRename(it.id, e)}
                        className="rounded p-1 text-heda-ink hover:bg-heda-line/50"
                        title="Save"
                      >
                        <Check size={11} />
                      </button>
                      <button
                        type="button"
                        data-testid={`render-rename-cancel-${it.id}`}
                        onClick={(e) => cancelRename(e)}
                        className="rounded p-1 text-heda-mute hover:bg-heda-line/50"
                        title="Cancel"
                      >
                        <X size={11} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <div
                        data-testid={`render-title-${it.id}`}
                        className="truncate text-[13px] font-semibold text-heda-ink"
                      >
                        {it.title || it.scene_name}
                      </div>
                      {(user.role === "owner" || it.created_by_id === user.id) && (
                        <button
                          type="button"
                          data-testid={`render-rename-btn-${it.id}`}
                          onClick={(e) => startRename(it, e)}
                          className="rounded p-1 text-heda-mute hover:text-heda-ink"
                          title="Rename"
                        >
                          <Pencil size={11} />
                        </button>
                      )}
                    </div>
                  )}
                  <div className="mt-0.5 flex items-center gap-1 truncate text-[10.5px] text-heda-mute">
                    <UserIcon size={9} />
                    <span className="truncate">{it.created_by_name || "—"}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1 truncate text-[10.5px] text-heda-mute">
                    <Calendar size={9} />
                    <span>{formatDateTime(it.created_at)}</span>
                  </div>
                  {it.styles?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {it.styles.slice(0, 3).map((s) => (
                        <span
                          key={s}
                          className="rounded-full bg-heda-olive-tint px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-heda-olive-dark"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {canDelete && (
                  <span
                    role="button"
                    tabIndex={-1}
                    onClick={(e) => remove(it.id, e)}
                    className="cursor-pointer rounded-full p-1.5 text-heda-mute hover:bg-red-50 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div
          data-testid="gallery-pagination"
          className="mt-6 flex flex-col items-center justify-between gap-3 border-t border-heda-line/60 pt-5 sm:flex-row"
        >
          <div className="text-[12px] text-heda-mute">
            Showing <span className="font-semibold text-heda-ink">{fromIdx}</span>–
            <span className="font-semibold text-heda-ink">{toIdx}</span> of{" "}
            <span className="font-semibold text-heda-ink">{total}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="gallery-prev"
              disabled={page === 0 || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="inline-flex items-center gap-1 rounded-full border border-heda-line bg-white px-3.5 py-1.5 text-[12.5px] font-medium text-heda-ink transition hover:border-heda-gold hover:text-heda-gold disabled:cursor-not-allowed disabled:opacity-40"
            >
              ← Prev
            </button>
            <span className="rounded-full bg-heda-gold-tint px-3 py-1 text-[12px] font-semibold text-heda-ink">
              Page {page + 1} of {totalPages}
            </span>
            <button
              data-testid="gallery-next"
              disabled={page + 1 >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              className="inline-flex items-center gap-1 rounded-full border border-heda-line bg-white px-3.5 py-1.5 text-[12.5px] font-medium text-heda-ink transition hover:border-heda-gold hover:text-heda-gold disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {activeLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <Loader2 size={32} className="animate-spin text-white" />
        </div>
      )}

      <Lightbox
        open={!!active}
        src={active?.image}
        renderId={active?.id}
        pinned={!!active?.pinned}
        hasPrev={activeIdx > 0}
        hasNext={activeIdx >= 0 && activeIdx < items.length - 1}
        onPrev={() => activeIdx > 0 && open(items[activeIdx - 1].id)}
        onNext={() =>
          activeIdx >= 0 && activeIdx < items.length - 1 && open(items[activeIdx + 1].id)
        }
        onTogglePin={
          active
            ? async () => {
                const next = !active.pinned;
                setActive({ ...active, pinned: next });
                setItems((prev) =>
                  prev.map((x) => (x.id === active.id ? { ...x, pinned: next } : x))
                );
                const r = await fetch(`${API}/renders/${active.id}/pin`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json", ...authHeaders() },
                  body: JSON.stringify({ pinned: next }),
                });
                if (!r.ok) {
                  // Revert on failure
                  setActive((cur) => (cur ? { ...cur, pinned: !next } : cur));
                  setItems((prev) =>
                    prev.map((x) => (x.id === active.id ? { ...x, pinned: !next } : x))
                  );
                }
              }
            : undefined
        }
        caption={
          active
            ? `${active.scene_name} · by ${active.created_by_name || "—"} · ${formatDateTime(active.created_at)}`
            : ""
        }
        onClose={() => setActive(null)}
        onDownload={downloadActive}
        onEdit={async (instruction) => {
          if (!active) return null;
          const r = await fetch(`${API}/edit`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders() },
            body: JSON.stringify({
              image_base64: active.image,
              instruction,
              scene_id: active.scene_id,
              parent_id: active.id,
            }),
          });
          if (r.status === 401) {
            localStorage.removeItem("hedaply_token");
            window.location.reload();
            return null;
          }
          if (r.status === 402) triggerCreditAlert();
          const data = await r.json().catch(() => ({}));
          if (!r.ok || !data.success || !data.image) {
            throw new Error(data.detail || "Edit failed. Try again.");
          }
          // Swap viewer to the new edited render so user can iterate further
          setActive({
            ...active,
            id: data.id,
            image: data.image,
            created_at: new Date().toISOString(),
          });
          clearCreditAlert();
          // Refresh gallery list so the new edited render appears
          load();
          return data.image;
        }}
      />
    </>
  );
}

/* ----------------------------- USERS ----------------------------- */
function UsersView() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  // Runtime-adjustable render caps (owner-only panel)
  const [caps, setCaps] = useState(null);
  const [capsPerUser, setCapsPerUser] = useState("");
  const [capsGlobal, setCapsGlobal] = useState("");
  const [capsSaving, setCapsSaving] = useState(false);
  const [capsMsg, setCapsMsg] = useState({ kind: "", text: "" });

  const loadCaps = useCallback(async () => {
    if (me?.role !== "owner") return;
    try {
      const res = await fetch(`${API}/settings/render-caps`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setCaps(data);
      setCapsPerUser(
        typeof data.per_user_24h === "number" ? String(data.per_user_24h) : ""
      );
      setCapsGlobal(
        typeof data.global_24h === "number" ? String(data.global_24h) : ""
      );
    } catch {
      /* silently ignore — panel just won't populate */
    }
  }, [me]);

  const saveCaps = async () => {
    setCapsSaving(true);
    setCapsMsg({ kind: "", text: "" });
    try {
      const body = {
        per_user_24h: capsPerUser === "" ? 0 : parseInt(capsPerUser, 10),
        global_24h: capsGlobal === "" ? 0 : parseInt(capsGlobal, 10),
      };
      const res = await fetch(`${API}/settings/render-caps`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not save caps");
      setCaps({
        ...(caps || {}),
        per_user_24h: data.per_user_24h,
        global_24h: data.global_24h,
        updated_at: data.updated_at,
        updated_by: data.updated_by,
      });
      setCapsMsg({ kind: "ok", text: "Saved." });
    } catch (ex) {
      setCapsMsg({ kind: "err", text: ex.message });
    } finally {
      setCapsSaving(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`${API}/users`, { headers: authHeaders() });
      const data = await res.json();
      setUsers(data.items || []);
    } catch {
      setErr("Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadCaps();
  }, [load, loadCaps]);

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email})?`)) return;
    const r = await fetch(`${API}/users/${u.id}`, { method: "DELETE", headers: authHeaders() });
    if (r.ok) load();
    else {
      const d = await r.json().catch(() => ({}));
      alert(d.detail || "Could not delete");
    }
  };

  const ownerCount = users.filter((u) => u.role === "owner").length;

  return (
    <div data-testid="users-view">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-[12px] uppercase tracking-wider text-heda-mute">
          {users.length} user{users.length === 1 ? "" : "s"} · {ownerCount} owner{ownerCount === 1 ? "" : "s"}
        </div>
        <button
          data-testid="add-user-btn"
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 rounded-full bg-heda-gold px-4 py-2 text-[13px] font-semibold text-white shadow-sm hover:bg-heda-gold-soft"
        >
          <Plus size={13} /> Add User
        </button>
      </div>

      {err && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {err}
        </div>
      )}

      {me.role === "owner" && caps && (
        <div
          data-testid="render-caps-panel"
          className="mb-4 rounded-2xl border border-heda-line bg-white p-4"
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">
              Daily render caps
            </div>
            {(caps.updated_at || caps.updated_by) && (
              <div className="text-[11px] text-heda-mute">
                Last changed
                {caps.updated_at ? ` ${formatDateTime(caps.updated_at)}` : ""}
                {caps.updated_by ? ` by ${caps.updated_by}` : ""}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-heda-mute">
                Per user (24h)
              </label>
              <input
                data-testid="caps-per-user-input"
                type="number"
                min={0}
                value={capsPerUser}
                onChange={(e) => setCapsPerUser(e.target.value)}
                className="w-28 rounded-xl border border-heda-line bg-heda-cream/40 px-3 py-2 text-[14px] outline-none focus:border-heda-gold"
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-heda-mute">
                Studio total (24h)
              </label>
              <input
                data-testid="caps-global-input"
                type="number"
                min={0}
                value={capsGlobal}
                onChange={(e) => setCapsGlobal(e.target.value)}
                className="w-28 rounded-xl border border-heda-line bg-heda-cream/40 px-3 py-2 text-[14px] outline-none focus:border-heda-gold"
              />
            </div>
            <button
              data-testid="caps-save-btn"
              type="button"
              onClick={saveCaps}
              disabled={capsSaving}
              className="inline-flex items-center gap-1.5 rounded-xl bg-heda-ink px-4 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-40"
            >
              {capsSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {capsSaving ? "Saving…" : "Save caps"}
            </button>
            {capsMsg.text && (
              <span
                data-testid="caps-save-msg"
                className={[
                  "text-[12px]",
                  capsMsg.kind === "ok" ? "text-heda-olive-dark" : "text-red-600",
                ].join(" ")}
              >
                {capsMsg.text}
              </span>
            )}
          </div>
          <p className="mt-2 text-[11px] text-heda-mute">
            0 means unlimited. Env defaults:{" "}
            per user {caps.env_default_per_user_24h}, studio total {caps.env_default_global_24h}.
          </p>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-heda-line bg-white">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={22} className="animate-spin text-heda-gold" />
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-heda-cream/60 text-[11px] uppercase tracking-wider text-heda-mute">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3 hidden sm:table-cell">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3 hidden md:table-cell">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const canDelete =
                  u.id !== me.id && (u.role !== "owner" || ownerCount > 1);
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-heda-line">
                    <td className="px-4 py-3">
                      <div className="font-medium text-heda-ink">
                        {u.name}
                        {u.id === me.id && (
                          <span className="ml-2 rounded-full bg-heda-cream px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-heda-mute">
                            you
                          </span>
                        )}
                        {typeof u.render_cap_override === "number" && (
                          <span
                            data-testid={`user-cap-override-marker-${u.id}`}
                            title={`Daily render limit: ${u.render_cap_override}`}
                            className="ml-2 rounded-full bg-heda-gold-tint px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-heda-gold"
                          >
                            cap {u.render_cap_override}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-heda-mute hidden sm:table-cell">{u.email}</td>
                    <td className="px-4 py-3">
                      <span
                        className={[
                          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                          u.role === "owner"
                            ? "bg-heda-gold/15 text-heda-gold"
                            : "bg-heda-olive-tint text-heda-olive-dark",
                        ].join(" ")}
                      >
                        {u.role === "owner" ? <Crown size={10} /> : <Shield size={10} />}
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-heda-mute hidden md:table-cell">
                      {formatDateTime(u.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          data-testid={`user-edit-${u.id}`}
                          onClick={() => setEditing(u)}
                          className="inline-flex items-center gap-1 rounded-full border border-heda-line px-2.5 py-1 text-[11px] text-heda-mute hover:border-heda-gold hover:text-heda-gold"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                        {canDelete && (
                          <button
                            data-testid={`user-delete-${u.id}`}
                            onClick={() => remove(u)}
                            className="inline-flex items-center gap-1 rounded-full border border-heda-line px-2.5 py-1 text-[11px] text-heda-mute hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 size={11} /> Remove
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && (
        <AddUserModal
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            load();
          }}
          creating={creating}
          setCreating={setCreating}
        />
      )}
      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
          ownerCount={ownerCount}
          isSelf={editing.id === me.id}
        />
      )}
    </div>
  );
}

function RolePicker({ value, onChange, lockedReason = "" }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {[
        { id: "staff", label: "Staff", icon: Shield, hint: "Visualizer + Gallery" },
        { id: "owner", label: "Owner", icon: Crown, hint: "Full access · users + analytics" },
      ].map((r) => {
        const active = value === r.id;
        const disabled = !!lockedReason;
        const Icon = r.icon;
        return (
          <button
            key={r.id}
            type="button"
            data-testid={`role-pick-${r.id}`}
            disabled={disabled}
            onClick={() => onChange(r.id)}
            className={[
              "flex flex-col items-start gap-0.5 rounded-xl border px-3 py-2.5 text-left transition",
              active
                ? "border-heda-gold bg-heda-gold/10 ring-1 ring-heda-gold/30"
                : "border-heda-line bg-white hover:border-heda-gold/40",
              disabled ? "cursor-not-allowed opacity-60" : "",
            ].join(" ")}
          >
            <span className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-heda-ink">
              <Icon size={12} /> {r.label}
            </span>
            <span className="text-[10.5px] text-heda-mute">{r.hint}</span>
          </button>
        );
      })}
    </div>
  );
}

function AddUserModal({ onClose, onCreated, creating, setCreating }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("staff");
  const [err, setErr] = useState("");
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setCreating(true);
    try {
      const res = await fetch(`${API}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ name: name.trim(), email: email.trim().toLowerCase(), password, role }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not create user");
      setCreated({ name: name.trim(), email: email.trim().toLowerCase(), password, role });
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setCreating(false);
    }
  };

  const copyCreds = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(
        `Hedaply AI login\nName: ${created.name}\nRole: ${created.role}\nEmail: ${created.email}\nPassword: ${created.password}\nLogin URL: ${window.location.origin}/`
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  return (
    <div
      data-testid="add-user-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl border border-heda-line bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-heda-line px-5 py-3">
          <h3 className="font-display text-lg font-bold text-heda-ink">
            {created ? "User added" : "Add user"}
          </h3>
          <button onClick={onClose} className="rounded-full p-1.5 text-heda-mute hover:bg-heda-cream/60">
            <X size={16} />
          </button>
        </div>

        {created ? (
          <div className="p-5">
            <div className="rounded-xl border border-heda-olive/30 bg-heda-olive-tint/40 p-4">
              <div className="flex items-center gap-2 text-heda-olive-dark">
                <Check size={16} />
                <span className="text-sm font-semibold">
                  Account created. Share these credentials with {created.name}:
                </span>
              </div>
              <div className="mt-3 space-y-1 rounded-lg bg-white p-3 text-[13px]">
                <div><span className="font-semibold">Role:</span> <span className="uppercase tracking-wider">{created.role}</span></div>
                <div><span className="font-semibold">Email:</span> {created.email}</div>
                <div><span className="font-semibold">Password:</span> {created.password}</div>
                <div className="text-heda-mute"><span className="font-semibold text-heda-ink">Login at:</span> {window.location.origin}/</div>
              </div>
              <button
                onClick={copyCreds}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-heda-ink px-3 py-1.5 text-[12px] font-medium text-white hover:bg-black"
              >
                <Copy size={11} /> {copied ? "Copied!" : "Copy credentials"}
              </button>
            </div>
            <button
              onClick={onCreated}
              className="mt-5 w-full rounded-xl bg-heda-gold px-4 py-3 text-sm font-semibold text-white hover:bg-heda-gold-soft"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            <div>
              <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Name</label>
              <div className="relative">
                <UserIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
                <input
                  data-testid="new-user-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-xl border border-heda-line bg-heda-cream/40 py-3 pl-9 pr-3 text-[15px] outline-none focus:border-heda-gold"
                  placeholder="Full name"
                  required
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Email</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
                <input
                  data-testid="new-user-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-heda-line bg-heda-cream/40 py-3 pl-9 pr-3 text-[15px] outline-none focus:border-heda-gold"
                  placeholder="staff@hedaplyai.com"
                  required
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
                <input
                  data-testid="new-user-password"
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl border border-heda-line bg-heda-cream/40 py-3 pl-9 pr-3 text-[15px] outline-none focus:border-heda-gold"
                  placeholder="Min 6 characters"
                  minLength={6}
                  required
                />
              </div>
              <p className="mt-1.5 text-[11px] text-heda-mute">Visible so you can copy and share.</p>
            </div>
            <div>
              <label className="mb-1.5 block text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Role</label>
              <RolePicker value={role} onChange={setRole} />
            </div>

            {err && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700">{err}</div>
            )}

            <button
              data-testid="new-user-submit"
              disabled={creating}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-heda-ink px-4 py-3 text-sm font-semibold text-white hover:bg-black disabled:opacity-40"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {creating ? "Creating…" : "Create account"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved, ownerCount, isSelf }) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(user.role || "staff");
  const initialCap =
    typeof user.render_cap_override === "number"
      ? String(user.render_cap_override)
      : "";
  const [capOverride, setCapOverride] = useState(initialCap);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const lastOwnerLock =
    user.role === "owner" && ownerCount <= 1
      ? "This is the last owner — promote another user to owner first to change this role."
      : "";

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      const body = {};
      if (name.trim() && name.trim() !== user.name) body.name = name.trim();
      if (email.trim() && email.trim().toLowerCase() !== user.email) body.email = email.trim().toLowerCase();
      if (password) body.password = password;
      if (role !== user.role) body.role = role;
      // Per-user cap override — send only when changed. Blank + originally
      // blank → omit entirely (untouched save must not rewrite). Blank +
      // originally set → send -1 to clear. Filled + changed → send number.
      if (capOverride !== initialCap) {
        if (capOverride === "") {
          body.render_cap_override = -1;
        } else {
          const n = parseInt(capOverride, 10);
          if (!Number.isNaN(n) && n >= 0) body.render_cap_override = n;
        }
      }
      if (Object.keys(body).length === 0) {
        onClose();
        return;
      }
      const res = await fetch(`${API}/users/${user.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not save");
      onSaved();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="edit-user-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl border border-heda-line bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-heda-line px-5 py-3">
          <div>
            <h3 className="font-display text-lg font-bold text-heda-ink">
              Edit user{isSelf && <span className="ml-2 rounded-full bg-heda-cream px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-heda-mute">you</span>}
            </h3>
            <p className="text-[11.5px] text-heda-mute">{user.email}</p>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 text-heda-mute hover:bg-heda-cream/60">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={save} className="p-5 space-y-4">
          <div>
            <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Name</label>
            <div className="relative">
              <UserIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
              <input
                data-testid="edit-user-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-xl border border-heda-line bg-heda-cream/40 py-3 pl-9 pr-3 text-[15px] outline-none focus:border-heda-gold"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Email</label>
            <div className="relative">
              <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
              <input
                data-testid="edit-user-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-heda-line bg-heda-cream/40 py-3 pl-9 pr-3 text-[15px] outline-none focus:border-heda-gold"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wider text-heda-mute">
              New password <span className="text-heda-mute/70">(leave blank to keep current)</span>
            </label>
            <div className="relative">
              <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-heda-mute" />
              <input
                data-testid="edit-user-password"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-heda-line bg-heda-cream/40 py-3 pl-9 pr-3 text-[15px] outline-none focus:border-heda-gold"
                placeholder="Min 6 characters"
                minLength={password ? 6 : undefined}
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Role</label>
            <RolePicker value={role} onChange={setRole} lockedReason={lastOwnerLock} />
            {lastOwnerLock && (
              <p className="mt-1.5 text-[11px] text-amber-700">{lastOwnerLock}</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold uppercase tracking-wider text-heda-mute">
              Daily render limit
            </label>
            <input
              data-testid="edit-user-cap-override"
              type="number"
              min={0}
              value={capOverride}
              onChange={(e) => setCapOverride(e.target.value)}
              className="w-full rounded-xl border border-heda-line bg-heda-cream/40 px-3 py-3 text-[15px] outline-none focus:border-heda-gold"
            />
            <p className="mt-1.5 text-[11px] text-heda-mute">
              Leave blank to use the global limit
            </p>
          </div>

          {err && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700">{err}</div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-heda-line bg-white px-4 py-2.5 text-sm font-medium text-heda-ink hover:bg-heda-cream/40"
            >
              Cancel
            </button>
            <button
              data-testid="edit-user-submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-xl bg-heda-ink px-4 py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-40"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ----------------------------- ADMIN PAGE ----------------------------- */
export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("gallery");
  const [ttlDays, setTtlDays] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/health`)
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled && d?.render_ttl_days) setTtlDays(d.render_ttl_days);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-heda-cream">
      <header className="sticky top-0 z-30 border-b border-heda-line bg-heda-cream/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-8 md:py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="back-to-app"
              className="hidden rounded-full border border-heda-line bg-white p-2 text-heda-ink transition hover:border-heda-gold hover:text-heda-gold sm:inline-flex"
              title="Back to visualizer"
            >
              <ArrowLeft size={15} />
            </Link>
            <img src="/heda-logo.png" alt="HEDA" className="h-9 w-auto sm:h-11" />
            <div className="leading-tight">
              <h1 className="font-display text-[17px] font-bold text-heda-ink sm:text-[19px]">
                Admin Gallery
              </h1>
              <p className="text-[10.5px] text-heda-mute">
                Auto-deletes after {ttlDays ?? 30} days · Signed in as <span className="font-semibold text-heda-ink/85">{user.name}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-1.5 rounded-full bg-heda-ink px-3 py-1.5 text-[12.5px] font-medium text-white hover:bg-black"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>

        {user.role === "owner" && (
          <div className="mx-auto flex max-w-7xl items-center gap-1 overflow-x-auto border-t border-heda-line/60 px-4 md:px-8">
            <button
              data-testid="tab-gallery"
              onClick={() => setTab("gallery")}
              className={[
                "relative shrink-0 px-3 py-2.5 text-[13px] font-semibold transition",
                tab === "gallery" ? "text-heda-ink" : "text-heda-mute hover:text-heda-ink",
              ].join(" ")}
            >
              <span className="inline-flex items-center gap-1.5">
                <ImageIcon size={13} /> Gallery
              </span>
              {tab === "gallery" && (
                <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-heda-gold" />
              )}
            </button>
            <button
              data-testid="tab-analytics"
              onClick={() => setTab("analytics")}
              className={[
                "relative shrink-0 px-3 py-2.5 text-[13px] font-semibold transition",
                tab === "analytics" ? "text-heda-ink" : "text-heda-mute hover:text-heda-ink",
              ].join(" ")}
            >
              <span className="inline-flex items-center gap-1.5">
                <BarChart3 size={13} /> Analytics
              </span>
              {tab === "analytics" && (
                <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-heda-gold" />
              )}
            </button>
            <button
              data-testid="tab-sessions"
              onClick={() => setTab("sessions")}
              className={[
                "relative shrink-0 px-3 py-2.5 text-[13px] font-semibold transition",
                tab === "sessions" ? "text-heda-ink" : "text-heda-mute hover:text-heda-ink",
              ].join(" ")}
            >
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck size={13} /> Sessions
              </span>
              {tab === "sessions" && (
                <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-heda-gold" />
              )}
            </button>
            <button
              data-testid="tab-users"
              onClick={() => setTab("users")}
              className={[
                "relative shrink-0 px-3 py-2.5 text-[13px] font-semibold transition",
                tab === "users" ? "text-heda-ink" : "text-heda-mute hover:text-heda-ink",
              ].join(" ")}
            >
              <span className="inline-flex items-center gap-1.5">
                <UsersIcon size={13} /> Users
              </span>
              {tab === "users" && (
                <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-heda-gold" />
              )}
            </button>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-10">
        {tab === "gallery" && <GalleryView user={user} ttlDays={ttlDays} />}
        {tab === "analytics" && user.role === "owner" && <Analytics />}
        {tab === "sessions" && user.role === "owner" && <Sessions />}
        {tab === "users" && user.role === "owner" && <UsersView />}
      </main>
    </div>
  );
}
